import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export interface Requirement {
  assign: number;
  score: number;
  excludeBenchmarkMonths: boolean;
}

export interface PendingRequirement extends Requirement {
  from: string;
}

export interface BenchmarkWindow {
  kind: string;
  start: string;
  close: string;
}

export type MonthPhase = 'future' | 'open' | 'closing' | 'final';

export interface MonthEntry {
  month: string;
  phase: MonthPhase;
  closeDate: string;
  skipped: boolean;
  window: BenchmarkWindow | null;
}

export interface SkippedMonth {
  month: string;
  window: BenchmarkWindow;
}

export interface PreviousCounts {
  month: string;
  closeDate: string;
  teachers: number;
  teachersMet: number;
}

export interface MonitoringCounts {
  teachers: number;
  teachersMet: number;
  students: number;
  assignedMet: number;
  scoredMet: number;
  previous: PreviousCounts | null;
}

export interface AssignmentMonitoringOverview {
  refreshedAt?: string;
  today: string;
  month: string;
  on: boolean;
  everOn: boolean;
  requirement: Requirement;
  drawerRequirement: Requirement;
  isDefault: boolean;
  pending: PendingRequirement | null;
  turnsOn?: string;
  stopsAfter?: string;
  offSince?: string;
  months: MonthEntry[];
  skippedMonths: SkippedMonth[];
  counts: MonitoringCounts | null;
  countsError?: boolean;
}

export type StudentStatus = 'met' | 'notAssigned' | 'notSubmitted' | 'notScored';

export interface Group {
  students: number;
  met: number;
  notAssigned: number;
  notSubmitted: number;
  notScored: number;
  assignedMet: number;
  scoredMet: number;
}

export interface StudentRow {
  id: string;
  firstName: string;
  lastName: string;
  grade: string;
  schoolId: string;
  teacherIds: string[];
  assigned: number;
  submitted: number;
  scored: number;
  status: StudentStatus;
  short: number;
  lastAssigned?: string;
  lastSubmitted?: string;
}

export interface TeacherSchool extends Group {
  schoolId: string;
  metAll: boolean;
}

export interface TeacherRow {
  id: string;
  name: string;
  email: string;
  students: number;
  met: number;
  group: Group;
  metAll: boolean;
  schools: TeacherSchool[];
}

export interface SchoolRow extends Group {
  id: string;
  name: string;
  teachers: number;
  teachersMet: number;
}

export interface DistrictGroup extends Group {
  teachers: number;
  teachersMet: number;
}

export interface MonthRecord {
  month: string;
  phase: MonthPhase;
  closeDate: string;
  requirement: Requirement;
  district: DistrictGroup;
  schools: SchoolRow[] | null;
  teachers: TeacherRow[] | null;
  students: StudentRow[] | null;
  windows: BenchmarkWindow[] | null;
  skipped: boolean;
  window: BenchmarkWindow | null;
  months: MonthEntry[];
}

export type SettingsAction = 'save' | 'turnOn' | 'turnOff' | 'cancelTurnOn' | 'keepOn';

export interface SettingsChange {
  action: SettingsAction;
  requirement?: Requirement;
}

export class AssignmentMonitoringError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AssignmentMonitoringError';
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}${path}`, {
    ...init,
    headers: { ...authHeaders(token), ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    throw new AssignmentMonitoringError(`assignment monitoring ${path} failed: ${response.status}`, response.status);
  }
  return (await response.json()) as T;
}

// Null when the caller has no district to monitor (a super admin outside an
// impersonation), so the dashboard shows no card.
export async function fetchAssignmentMonitoring(): Promise<AssignmentMonitoringOverview | null> {
  try {
    return await request<AssignmentMonitoringOverview>('/assignment-monitoring');
  } catch (err) {
    if (err instanceof AssignmentMonitoringError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function saveAssignmentMonitoringSettings(
  change: SettingsChange,
): Promise<AssignmentMonitoringOverview> {
  return request<AssignmentMonitoringOverview>('/assignment-monitoring/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(change),
  });
}

export async function fetchAssignmentMonitoringRecord(month: string): Promise<MonthRecord> {
  return request<MonthRecord>(`/assignment-monitoring/records/${encodeURIComponent(month)}`);
}
